import { applyTimezone, parseDate } from '../helpers/date';
import { DjangoUser, User, parseUser } from '../user';
import { DjangoEmployee, Employee, parseEmployee } from './../employee';
import { AbstractLoan } from './../loan/abstract-loan.model';

export class Simulation implements AbstractLoan {
  id: string;
  employee: Employee;
  user: User;
  taxAmount: number;
  installmentAmount: number;
  dueDate: Date;
  firstReferencePeriod: string;
  lastReferencePeriod: Date;
  firstDueDate: Date;
  lastDueDate: Date;
  firstPendingInstallmentDueDate: Date;
  lastPendingInstallmentDueDate: Date;
  // GETTERS
  totalDebtAmount: number;
  financedAmount: number;
  // ABSTRACT LOAN
  modified: Date;
  created: Date;
  amount: number;
  period: number;
  interestRate: number;
  effectiveInterestRate: number;
  feeAmount: number;
  currency: string;
  score: number;
  scoreVersion: number;
  agioAmount: number;
  isRefi: boolean;
  insuranceInstallmentAmount: number;

  constructor(data: string | Simulation | DjangoSimulation = {}) {
    if (data instanceof Simulation) {
      bindSimulationData(this, data);
    } else if (typeof data === 'string') {
      bindDjangoSimulationData(this, { id: data });
    } else {
      bindDjangoSimulationData(this, data);
    }
  }
}

export function parseSimulation(data: string | DjangoSimulation | Simulation) {
  if (data) {
    return new Simulation(data);
  } else {
    return undefined;
  }
}

function bindSimulationData(simulation: Simulation, data: Simulation) {
  simulation.id = data.id;
  simulation.employee = data.employee;
  simulation.user = data.user;
  simulation.taxAmount = data.taxAmount;
  simulation.firstReferencePeriod = data.firstReferencePeriod;
  simulation.lastReferencePeriod = data.lastReferencePeriod;
  simulation.installmentAmount = data.installmentAmount;
  simulation.dueDate = data.dueDate;
  simulation.firstDueDate = data.firstDueDate;
  simulation.lastDueDate = data.lastDueDate;
  simulation.firstPendingInstallmentDueDate = data.firstPendingInstallmentDueDate;
  simulation.lastPendingInstallmentDueDate = data.lastPendingInstallmentDueDate;
  // GETTERS
  simulation.totalDebtAmount = data.totalDebtAmount;
  simulation.financedAmount = data.financedAmount;
  // ABSTRACT LOAN
  simulation.modified = data.modified;
  simulation.created = data.created;
  simulation.amount = data.amount;
  simulation.period = data.period;
  simulation.interestRate = data.interestRate;
  simulation.effectiveInterestRate = data.effectiveInterestRate;
  simulation.feeAmount = data.feeAmount;
  simulation.currency = data.currency;
  simulation.score = data.score;
  simulation.scoreVersion = data.scoreVersion;
  simulation.agioAmount = data.agioAmount;
  simulation.isRefi = data.isRefi;
  simulation.insuranceInstallmentAmount = data.insuranceInstallmentAmount;
}

////////////////////
// DJANGO SUPPORT //
////////////////////
export interface DjangoSimulation {
  id?: string;
  employee?: string | DjangoEmployee;
  user?: string | DjangoUser;
  tax_amount?: number;
  installment_amount?: number;
  due_date?: string;
  first_reference_period?: string;
  last_reference_period?: string;
  first_due_date?: string;
  last_due_date?: string;
  first_pending_installment_due_date?: string;
  last_pending_installment_due_date?: string;
  // GETTERS
  total_debt_amount?: number;
  financed_amount?: number;
  // ABSTRACT LOAN
  modified?: string;
  created?: string;
  amount?: number;
  period?: number;
  interest_rate?: number;
  effective_interest_rate?: number;
  fee_amount?: number;
  currency?: string;
  score?: number;
  score_version?: number;
  agio_amount?: number;
  is_refi?: boolean;
  insurance_installment_amount?: number;
}

function bindDjangoSimulationData(simulation: Simulation, data: DjangoSimulation) {
  simulation.id = data.id;
  simulation.employee = parseEmployee(data.employee);
  simulation.user = parseUser(data.user);
  simulation.taxAmount = Number(data.tax_amount);
  simulation.installmentAmount = Number(data.installment_amount);
  simulation.firstReferencePeriod = data.first_reference_period;
  simulation.lastReferencePeriod = applyTimezone(parseDate(data.last_reference_period), 0);
  simulation.dueDate = applyTimezone(parseDate(data.due_date), 0);
  simulation.firstDueDate = applyTimezone(parseDate(data.first_due_date), 0);
  simulation.lastDueDate = applyTimezone(parseDate(data.last_due_date), 0);
  simulation.firstPendingInstallmentDueDate = applyTimezone(
    parseDate(data.first_pending_installment_due_date),
    0,
  );
  simulation.lastPendingInstallmentDueDate = applyTimezone(
    parseDate(data.last_pending_installment_due_date),
    0,
  );
  // GETTERS
  simulation.totalDebtAmount = Number(data.total_debt_amount);
  simulation.financedAmount = Number(data.financed_amount);
  // ABSTRACT LOAN
  simulation.modified = parseDate(data.modified);
  simulation.created = parseDate(data.created);
  simulation.amount = Number(data.amount);
  simulation.period = Number(data.period);
  simulation.interestRate = Number(data.interest_rate);
  simulation.effectiveInterestRate = Number(data.effective_interest_rate);
  simulation.feeAmount = Number(data.fee_amount);
  simulation.currency = data.currency;
  simulation.score = Number(data.score);
  simulation.scoreVersion = Number(data.score_version);
  simulation.agioAmount = Number(data.agio_amount);
  simulation.isRefi = Boolean(data.is_refi);
  simulation.insuranceInstallmentAmount = Number(data.insurance_installment_amount);
}
